import { supabase } from '@/integrations/supabase/client';
import { AnalysisResult } from '@/types/naturopathy';

export class ConsultationService {
  async saveConsultation(patientId: string, analysis: AnalysisResult, symptomsInput: string) {
    try {
      // 1. Insert consultation
      const { data: consultation, error: consultationError } = await supabase
        .from('consultations')
        .insert({
          patient_id: patientId,
          symptoms_text: symptomsInput,
          triage_level: analysis.triage.level,
          triage_message: analysis.triage.message || null,
          red_flags: analysis.triage.redFlags,
          explanations: analysis.explanations,
          confidence_score: analysis.confidence_score
        })
        .select()
        .single();

      if (consultationError) throw consultationError;

      // 2. Insert recommendations
      if (analysis.recommendations.length > 0) {
        const { error: recError } = await supabase
          .from('recommendations')
          .insert(
            analysis.recommendations.map(rec => ({
              consultation_id: consultation.id,
              type: rec.type,
              title: rec.title,
              detail: rec.detail,
              posology: rec.posology || null,
              contraindications: rec.contraindications,
              priority: rec.priority,
              confidence: rec.confidence
            }))
          );

        if (recError) throw recError;
      }

      // 3. Insert interaction alerts
      if (analysis.interactions_alert.length > 0) {
        const { error: intError } = await supabase
          .from('interaction_alerts')
          .insert(
            analysis.interactions_alert.map(alert => ({
              consultation_id: consultation.id,
              medication: alert.medication,
              plant: alert.plant,
              severity: alert.severity,
              description: alert.description
            }))
          );

        if (intError) throw intError;
      }

      // 4. Insert sources
      if (analysis.sources.length > 0) {
        const { error: sourcesError } = await supabase
          .from('sources')
          .insert(
            analysis.sources.map(source => ({
              consultation_id: consultation.id,
              title: source.title,
              url: source.url || null,
              summary: source.summary,
              reliability: source.reliability
            }))
          );

        if (sourcesError) throw sourcesError;
      }

      return { success: true, consultationId: consultation.id };
    } catch (error) {
      console.error('Error saving consultation:', error);
      return { success: false, error };
    }
  }

  async getPatients() {
    const { data, error } = await supabase
      .from('patients')
      .select('id, name, email, date_of_birth')
      .order('name');

    if (error) {
      console.error('Error fetching patients:', error);
      return [];
    }

    return data || [];
  }
}

export const consultationService = new ConsultationService();
